import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Nav from "../../../components/Nav";
import PayWidgetEvents from "../../../components/PayWidgetEvents";
import {
  chargedButIncompleteMessage,
  readBundleChargedButIncomplete,
} from "../../../utils/bundlePurchase";
import { useTrackEvent } from "../../../hooks/useTrackEvent";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faCircleMinus,
  faStar,
} from "@fortawesome/free-solid-svg-icons";

const getBundleId = (bundle) => bundle?.combineBundleId;
const getBundleName = (bundle) => bundle?.name ?? "Combine Bundle";
const getCombines = (bundle) =>
  Array.isArray(bundle?.combines) ? bundle.combines : [];
const getBundleCombineCount = (bundle) =>
  bundle?.combineCount ?? getCombines(bundle).length;
const getTotalSeparatePrice = (bundle) =>
  Number(bundle?.totalIfBoughtSeparately ?? 0);
const getBundlePrice = (bundle) => Number(bundle?.bundlePrice ?? 0);
const getSavingsAmount = (bundle) => Number(bundle?.savingsAmount ?? 0);
const getSavingsPercent = (bundle) => Number(bundle?.savingsPercent ?? 0);
const getDrillCombineCount = (bundle) =>
  bundle?.combinesWithPositionDrillsCount ??
  getCombines(bundle).filter(getHasPositionDrills).length;

const getCombineEventId = (combine) => combine?.eventId;
const getCombineName = (combine) => combine?.name ?? "Combine";
const getCombineLocation = (combine) =>
  [combine?.city, combine?.state].filter(Boolean).join(", ");
const getCombinePrice = (combine) => Number(combine?.combinePrice ?? 0);
const getHasPositionDrills = (combine) => Boolean(combine?.hasPositionDrills);
const getPositionDrillPrice = (combine) =>
  Number(combine?.positionDrillPrice ?? 0);
const isSelectedCombine = (combine) => Boolean(combine?.isSelected);

const formatMoney = (value) => `$${Number(value ?? 0).toFixed(2)}`;
const formatSavings = (value) => {
  const amount = Number(value ?? 0);
  return Number.isInteger(amount) ? `$${amount}` : formatMoney(amount);
};
const pluralize = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;
const pad2 = (value) => String(value ?? 0).padStart(2, "0");

const formatDate = (date) => {
  if (!date) return "";
  if (typeof date === "object") {
    if (typeof date.year !== "number") return "";
    return `${pad2(date.month)}-${pad2(date.day)}-${date.year}`;
  }
  const [year, month, day] = String(date).split("T")[0].split("-");
  if (!year || !month || !day) return "";
  return `${month}-${day}-${year}`;
};

const formatTime = (time) => {
  if (!time) return "";
  let hour;
  let minute;
  if (typeof time === "object") {
    hour = time.hour;
    minute = time.minute;
  } else {
    [hour, minute] = String(time).split(":").map(Number);
  }
  if (typeof hour !== "number" || Number.isNaN(hour)) return "";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  const period = hour >= 12 ? "PM" : "AM";
  return `${hour12}:${pad2(Number.isNaN(minute) ? 0 : minute)} ${period}`;
};

const getCombineDate = (combine) => formatDate(combine?.date);

const getCombineTime = (combine) => {
  const start = formatTime(combine?.startTime);
  const end = formatTime(combine?.endTime);
  if (start && end) return `${start} - ${end}`;
  return start || end;
};

const Icon = ({ className = "h-4 w-4", children }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`flex-shrink-0 ${className}`}
    aria-hidden="true"
  >
    {children}
  </svg>
);

const CalendarIcon = ({ className }) => (
  <Icon className={className}>
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </Icon>
);

const ClockIcon = ({ className }) => (
  <Icon className={className}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
  </Icon>
);

const PinIcon = ({ className }) => (
  <Icon className={className}>
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
    <circle cx="12" cy="10" r="3" />
  </Icon>
);

const TrophyIcon = ({ className }) => (
  <Icon className={className}>
    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6M18 9h1.5a2.5 2.5 0 0 0 0-5H18M4 22h16" />
    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
  </Icon>
);

const TagIcon = ({ className }) => (
  <Icon className={className}>
    <path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z" />
    <circle cx="7.5" cy="7.5" r="1" />
  </Icon>
);

const DollarIcon = ({ className }) => (
  <Icon className={className}>
    <circle cx="12" cy="12" r="10" />
    <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8M12 18V6" />
  </Icon>
);

const InfoIcon = ({ className }) => (
  <Icon className={className}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-5M12 8h.01" />
  </Icon>
);

const UsersIcon = ({ className }) => (
  <Icon className={className}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
  </Icon>
);

const ArrowLeftIcon = ({ className }) => (
  <Icon className={className}>
    <path d="m12 19-7-7 7-7M19 12H5" />
  </Icon>
);

const CloseIcon = ({ className }) => (
  <Icon className={className}>
    <path d="M18 6 6 18M6 6l12 12" />
  </Icon>
);

const CombineDetails = ({ combine }) => (
  <div className="text-sm text-gray-400 flex flex-wrap gap-x-5 gap-y-1 mt-1.5">
    {getCombineDate(combine) && (
      <span className="flex items-center gap-2">
        <CalendarIcon />
        {getCombineDate(combine)}
      </span>
    )}
    {getCombineTime(combine) && (
      <span className="flex items-center gap-2">
        <ClockIcon />
        {getCombineTime(combine)}
      </span>
    )}
    {getCombineLocation(combine) && (
      <span className="flex items-center gap-2">
        <PinIcon />
        {getCombineLocation(combine)}
      </span>
    )}
  </div>
);

const IncludedChip = ({ children }) => (
  <span className="inline-flex items-center gap-2 bg-green-500/10 text-gray-200 text-sm rounded px-2.5 py-1">
    <FontAwesomeIcon icon={faCircleCheck} className="h-4 w-4 text-green-400" />
    {children}
  </span>
);

const OptionalChip = ({ children }) => (
  <span className="inline-flex items-center gap-2 bg-gray-700 text-gray-200 text-sm rounded px-2.5 py-1">
    <FontAwesomeIcon icon={faCircleMinus} className="h-4 w-4 text-gray-300" />
    {children}
  </span>
);

const BundleCombineRow = ({ combine, isSelected }) => (
  <div
    data-testid={`bundle-combine-${getCombineEventId(combine)}`}
    className="border border-gray-600 rounded-lg px-4 sm:px-6 py-3 bg-[#363636]"
  >
    {isSelected && (
      <div className="text-xs font-semibold text-red-500 uppercase mb-0.5">
        Your Selected Combine (Included)
      </div>
    )}
    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
      <div className="min-w-0">
        <div className="font-semibold text-lg text-white break-words">
          {getCombineName(combine)}
        </div>
        <CombineDetails combine={combine} />
      </div>
      <div className="flex flex-col sm:items-end gap-1.5 flex-shrink-0">
        <span className="text-lg font-bold text-white">
          {formatMoney(getCombinePrice(combine))}
        </span>
        {getHasPositionDrills(combine) && (
          <IncludedChip>
            Position Drill included (+
            {formatSavings(getPositionDrillPrice(combine))} value)
          </IncludedChip>
        )}
      </div>
    </div>
  </div>
);

const RadioOption = ({ value, checked, onChange, children }) => (
  <label
    className={`flex items-start gap-4 cursor-pointer rounded-lg border-2 px-4 sm:px-5 py-4 ${
      checked ? "border-red-500 bg-red-500/10" : "border-gray-600 bg-[#363636]"
    }`}
  >
    <input
      type="radio"
      name="eventOption"
      value={value}
      checked={checked}
      onChange={() => onChange(value)}
      className="mt-1 h-6 w-6 flex-shrink-0 accent-red-500 cursor-pointer"
    />
    <div className="flex-1 min-w-0">{children}</div>
  </label>
);

const OPTION_SINGLE = "single";
const OPTION_BUNDLE = "bundle";

const CombineBundleStep = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const trackEvent = useTrackEvent();

  const { eventId, bundle } = location.state || {};
  const combines = getCombines(bundle);
  const selectedCombine =
    combines.find(isSelectedCombine) ??
    combines.find(
      (combine) => String(getCombineEventId(combine)) === String(eventId),
    );
  const remainingCombines = combines.filter(
    (combine) => combine !== selectedCombine,
  );
  const selectedHasDrills = getHasPositionDrills(selectedCombine);
  const drillCombineCount = getDrillCombineCount(bundle);
  const combineCount = getBundleCombineCount(bundle);
  const savingsAmount = getSavingsAmount(bundle);
  const savingsPercent = Math.round(getSavingsPercent(bundle));

  const singlePrice = getCombinePrice(selectedCombine);
  const selectedDrillPrice = getPositionDrillPrice(selectedCombine);

  const [option, setOption] = useState(OPTION_SINGLE);
  const [isIntellipayReady, setIsIntellipayReady] = useState(false);
  // Card charged but registration incomplete. Kept separate from
  // payBundleHere: falling back to Continue would send the athlete to
  // Payment.jsx, where the bundle could be charged again.
  const [chargedButIncomplete, setChargedButIncomplete] = useState(
    readBundleChargedButIncomplete,
  );

  const canShowOffer = Boolean(bundle && selectedCombine);
  // A paid bundle is charged straight from this screen: Upgrade to Bundle is
  // the IntelliPay submit button. A zero-price bundle has nothing to charge,
  // so it still goes through Payment.jsx's free Register path.
  const payBundleHere = option === OPTION_BUNDLE && getBundlePrice(bundle) > 0;
  const registeredAccount = JSON.parse(
    localStorage.getItem("registeredAccount") || "{}",
  );

  useEffect(() => {
    if (!canShowOffer) {
      navigate("/athlete-membership", {
        state: { fromSpotRegister: true },
        replace: true,
      });
    }
  }, [canShowOffer, navigate]);

  useEffect(() => {
    if (!canShowOffer) return;
    trackEvent("Registration2_5_ViewedCombineBundle", {
      event_id: eventId,
      bundle_id: getBundleId(bundle),
      bundle_price: getBundlePrice(bundle),
      total_separate_price: getTotalSeparatePrice(bundle),
      combine_count: combines.length,
      combines_with_position_drills: getDrillCombineCount(bundle),
    });
  }, [canShowOffer, bundle, eventId, combines.length, trackEvent]);

  if (!canShowOffer) return null;

  const handleDecline = () => {
    trackEvent("Registration2_6_DeclinedCombineBundle", {
      event_id: eventId,
      bundle_id: getBundleId(bundle),
    });

    localStorage.removeItem("bundleDetails");

    trackEvent("Registration4_OpenedPaymentModal", { event_id: eventId });

    navigate("/athlete-membership", {
      state: { fromSpotRegister: true },
    });
  };

  const bundleChoice = {
    bundleId: getBundleId(bundle),
    bundlePrice: getBundlePrice(bundle),
    combines,
  };

  const recordAcceptance = () => {
    trackEvent("Registration2_6_AcceptedCombineBundle", {
      event_id: eventId,
      bundle_id: getBundleId(bundle),
      bundle_price: getBundlePrice(bundle),
    });
    localStorage.removeItem("appliedCouponDiscount");
    localStorage.removeItem("appliedCouponCode");
    localStorage.removeItem("appliedCouponType");

    localStorage.setItem(
      "subscriptionDetails",
      JSON.stringify({
        combine: true,
        positionDrill: false,
      }),
    );
    localStorage.setItem("bundleDetails", JSON.stringify(bundleChoice));
  };

  const handleAccept = () => {
    recordAcceptance();
    window.location.href = "/register/subscription";
  };

  // Runs alongside IntelliPay's own handler on the same click, which opens
  // the payment modal; nothing here may navigate away from the widget.
  const handlePayBundle = (e) => {
    if (!isIntellipayReady) {
      e.preventDefault();
      toast.info("IntelliPay is loading… please wait.", {
        position: "top-right",
      });
      return;
    }
    recordAcceptance();
  };

  const handleContinue = () => {
    if (option === OPTION_BUNDLE) {
      handleAccept();
    } else {
      handleDecline();
    }
  };

  const continueLabel =
    option === OPTION_BUNDLE
      ? `Upgrade to Bundle – ${formatMoney(getBundlePrice(bundle))}`
      : "Continue with Single Event";

  return (
    <>
      <Nav />
      <div className="min-h-screen bg-[#363636] w-full px-4 py-8 sm:py-10">
        <div className="w-full max-w-6xl mx-auto bg-[#1B1B1B] text-white rounded-lg shadow-xl">
          <div className="px-5 sm:px-10 pt-6 sm:pt-8">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h1 className="text-2xl sm:text-4xl font-black italic uppercase text-white">
                  Event Registration
                </h1>
                {selectedCombine && (
                  <p className="text-sm sm:text-base text-gray-400 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="uppercase">
                      {getCombineName(selectedCombine)}
                    </span>
                    {getCombineDate(selectedCombine) && (
                      <>
                        <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                        <span>{getCombineDate(selectedCombine)}</span>
                      </>
                    )}
                    {getCombineLocation(selectedCombine) && (
                      <>
                        <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                        <span>{getCombineLocation(selectedCombine)}</span>
                      </>
                    )}
                  </p>
                )}
              </div>
              <div className="flex items-center pt-1 sm:pt-3 flex-shrink-0">
                <button
                  type="button"
                  onClick={handleDecline}
                  title="Close"
                  aria-label="Close"
                  className="text-gray-300 hover:text-white cursor-pointer"
                >
                  <CloseIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 px-5 sm:px-10 py-6">
            <div className="lg:col-span-3 min-w-0">
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="text-xl sm:text-2xl font-bold text-white">
                  Choose Your Event Options
                </h2>
                <span className="inline-flex items-center gap-2 bg-red-500 text-white text-sm font-medium uppercase px-3 py-1 rounded">
                  <FontAwesomeIcon icon={faStar} className="h-3.5 w-3.5" />
                  Best Value
                </span>
              </div>
              <p className="text-gray-300 mt-2">
                Choose one option below. Add more combines and save money with a
                bundle.
              </p>

              <div className="bg-red-500/10 border border-red-500/40 rounded-lg px-4 sm:px-5 py-3 flex items-center gap-4 mt-4">
                <InfoIcon className="h-9 w-9 text-red-500" />
                <div>
                  <p className="text-white">
                    Your current registration is already included in this
                    bundle.
                  </p>
                  {remainingCombines.length > 0 && (
                    <p className="text-sm text-gray-300">
                      Add {pluralize(remainingCombines.length, "more combine")}
                      {savingsAmount > 0 && (
                        <>
                          {" "}
                          and save{" "}
                          <span className="font-bold">
                            {formatSavings(savingsAmount)}
                          </span>
                        </>
                      )}
                      .
                      {drillCombineCount > 0 &&
                        " All available position drills will be included."}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-6 mt-6">
                <RadioOption
                  value={OPTION_SINGLE}
                  checked={option === OPTION_SINGLE}
                  onChange={setOption}
                >
                  <div className="flex justify-between gap-4">
                    <div className="min-w-0">
                      <span className="font-semibold text-lg text-white">
                        Single Event
                      </span>
                      <p className="text-gray-300 mt-1">
                        Register for this combine.
                      </p>
                    </div>
                    <span className="text-xl font-bold text-white whitespace-nowrap">
                      {formatMoney(singlePrice)}
                    </span>
                  </div>
                  {selectedHasDrills && (
                    <div className="mt-2">
                      <OptionalChip>
                        Add Position Drill for +
                        {formatSavings(selectedDrillPrice)} (optional)
                      </OptionalChip>
                    </div>
                  )}
                </RadioOption>
              </div>

              <div className="border-t border-gray-600 mt-6 pt-4">
                <label className="flex items-start gap-4 cursor-pointer">
                  <input
                    type="radio"
                    name="eventOption"
                    value={OPTION_BUNDLE}
                    checked={option === OPTION_BUNDLE}
                    onChange={() => setOption(OPTION_BUNDLE)}
                    aria-label="Combine bundle"
                    className="mt-0.5 h-6 w-6 flex-shrink-0 accent-red-500 cursor-pointer"
                  />
                  <div className="min-w-0">
                    <div className="font-semibold text-red-500 uppercase">
                      Add {pluralize(remainingCombines.length, "More Combine")}{" "}
                      To Complete Your Bundle
                    </div>
                    <p className="text-gray-300">
                      {drillCombineCount > 0 ? (
                        <>
                          These combines will include the position drills (where
                          available) at{" "}
                          <span className="text-red-500">no extra cost</span>.
                        </>
                      ) : (
                        `Includes ${pluralize(remainingCombines.length, "additional combine")}.`
                      )}
                    </p>
                  </div>
                </label>

                <div
                  className={`mt-4 max-h-96 overflow-y-auto space-y-4 rounded-lg ${
                    option === OPTION_BUNDLE ? "ring-2 ring-red-500 p-2" : ""
                  }`}
                >
                  {selectedCombine && (
                    <BundleCombineRow combine={selectedCombine} isSelected />
                  )}
                  {remainingCombines.map((combine) => (
                    <BundleCombineRow
                      key={getCombineEventId(combine)}
                      combine={combine}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:col-span-2 border border-gray-600 rounded-lg px-6 py-6 h-fit">
              <div className="text-center border-b border-gray-600 pb-7">
                <h3 className="text-xl sm:text-2xl font-bold text-white">
                  {getBundleName(bundle)}
                </h3>
                <p className="text-gray-200 mt-1">
                  {pluralize(combineCount, "Combine")}
                  {drillCombineCount > 0 &&
                    ` + ${pluralize(drillCombineCount, "Position Drill")}`}
                </p>
              </div>

              <div className="flex justify-between gap-4 font-bold text-white text-lg border-b border-gray-600 py-5">
                <span>Total if purchased separately</span>
                <span>{formatMoney(getTotalSeparatePrice(bundle))}</span>
              </div>

              <div className="flex justify-between items-center gap-4 mt-8">
                <span className="text-lg font-bold text-white">
                  Bundle Price
                </span>
                <span className="text-3xl sm:text-4xl font-bold text-white">
                  {formatMoney(getBundlePrice(bundle))}
                </span>
              </div>

              {savingsAmount > 0 && (
                <div className="bg-green-500/10 rounded-md px-3 py-4 mt-6 flex items-center justify-center gap-4 text-lg font-semibold text-white">
                  <TagIcon className="h-6 w-6" />
                  <span>
                    You Save {formatSavings(savingsAmount)}
                    {savingsPercent > 0 && ` (${savingsPercent}% Off)`}
                  </span>
                </div>
              )}

              <ul className="text-white space-y-5 border-t border-gray-600 pt-7 mt-8">
                <li className="flex items-center gap-4">
                  <TrophyIcon className="h-7 w-7" />
                  Maximize your exposure
                </li>
                <li className="flex items-center gap-4">
                  <CalendarIcon className="h-7 w-7" />
                  Compete in {combineCount} high-level events
                </li>
                {drillCombineCount > 0 && (
                  <li className="flex items-center gap-4">
                    <DollarIcon className="h-7 w-7" />
                    All position drills included in bundle
                  </li>
                )}
                <li className="flex items-center gap-4">
                  <UsersIcon className="h-7 w-7" />
                  Best value for serious athletes
                </li>
              </ul>
            </div>
          </div>

          {chargedButIncomplete && (
            <div
              role="alert"
              className="mx-5 sm:mx-10 mb-5 bg-red-500/10 border border-red-500/40 rounded-lg px-4 sm:px-5 py-3 text-white"
            >
              {chargedButIncompleteMessage(chargedButIncomplete.transactionId)}
            </div>
          )}

          <div className="border-t border-gray-600 px-5 sm:px-10 py-5 flex flex-col-reverse sm:flex-row sm:justify-between sm:items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="border-2 border-gray-400 text-white font-semibold rounded px-6 py-3 hover:bg-[#363636] cursor-pointer flex items-center justify-center gap-3"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              Previous
            </button>
            {chargedButIncomplete ? (
              <button
                key="charged-but-incomplete"
                type="button"
                disabled
                className="bg-gray-500 text-white uppercase font-semibold rounded px-8 py-3.5 cursor-not-allowed opacity-70"
              >
                Payment received – contact support
              </button>
            ) : payBundleHere ? (
              <button
                key="pay-bundle"
                type="button"
                data-ipayname="submit"
                disabled={!isIntellipayReady}
                aria-busy={!isIntellipayReady}
                onClick={handlePayBundle}
                className={`ipayfield text-white uppercase font-semibold rounded px-8 py-3.5 ${
                  isIntellipayReady
                    ? "bg-red-500 hover:bg-red-700 cursor-pointer"
                    : "bg-gray-500 cursor-not-allowed opacity-70"
                }`}
              >
                {isIntellipayReady ? continueLabel : "Loading…"}
              </button>
            ) : (
              <button
                key="continue"
                type="button"
                onClick={handleContinue}
                className="bg-red-500 text-white uppercase font-semibold rounded px-8 py-3.5 hover:bg-red-700 cursor-pointer"
              >
                {continueLabel}
              </button>
            )}
          </div>
        </div>
      </div>

      {payBundleHere && !chargedButIncomplete && (
        <PayWidgetEvents
          userEmail={registeredAccount.email}
          userId={registeredAccount.id}
          paymentAmount={getBundlePrice(bundle)}
          eventId={getCombineEventId(selectedCombine)}
          subscriptionType="bundle"
          bundleChoice={bundleChoice}
          onIntellipayReady={setIsIntellipayReady}
          onChargedButIncomplete={(transactionId) =>
            setChargedButIncomplete({ transactionId })
          }
        />
      )}
    </>
  );
};

export default CombineBundleStep;
